import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination, Select } from 'antd';
import { FONT_SIZE, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';

// ─── 가격 조회 화면 표 공용 페이지 넘김(2026-10-26, 감사 card-controltower#115 · #116) ─────────────────────
//
// antd Table 의 내장 pagination 은 표 아래 한 곳에만 그려지고, 크기 선택("50 / 페이지")과 번호 사이 간격이 화면 폭에
// 따라 붙어 보였다(#115). 가격 조회 화면의 표(하락 검토 탭 · PriceHub 변경 탭)는 Table 의 pagination 을 끄고 이 줄을
// 표 위 · 아래에 직접 그린다:
//   - "1~50 / 92" 범위 표시, 크기 50 · 100 · 200 · 300 · 500 · 1000(사용자 결정 2026-10-26, 기본 50)
//   - 크기는 localStorage 에 남겨 탭 · 필터를 바꿔도 · 다시 들어와도 그대로(같은 화면의 두 표가 함께 따른다)
//   - ← → 키로 앞 · 뒤 페이지(입력칸 · 탭 목록 · 열린 창에서는 끈다)
//   - 체크한 줄이 다른 페이지에 있으면 "다른 페이지 N건 선택"

export const TABLE_PAGE_SIZES = [50, 100, 200, 300, 500, 1000] as const;
export const DEFAULT_TABLE_PAGE_SIZE = 50;
/** 가격 조회 화면 표들이 함께 쓰는 크기 저장 키. */
export const DAILY_TABLE_PAGE_SIZE_KEY = 'cct.dailyTable.pageSize';
const SIZE_EVENT = 'cct:table-page-size';

function readPageSize(key: string): number {
  try {
    const v = Number(localStorage.getItem(key));
    return (TABLE_PAGE_SIZES as readonly number[]).includes(v) ? v : DEFAULT_TABLE_PAGE_SIZE;
  } catch {
    return DEFAULT_TABLE_PAGE_SIZE;
  }
}

function writePageSize(key: string, size: number) {
  try { localStorage.setItem(key, String(size)); } catch { /* 저장 못 해도 이번 화면에서는 바뀐다 */ }
}

export interface TablePaging<T> {
  page: number;
  pageSize: number;
  total: number;
  maxPage: number;
  /** 지금 페이지에 그릴 줄. */
  pageRows: T[];
  /** 보이는 페이지가 바뀔 때마다 달라지는 값(페이지 · 크기 · resetKey) — 표 스크롤을 맨 위로 되돌릴 때 쓴다. */
  viewKey: string;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

/**
 * 받은 줄을 페이지로 자른다. `resetKey` 가 바뀌면(탭 · 필터) 1페이지로 돌아가고, 줄이 줄어 지금 페이지가 비면 마지막
 * 페이지로 당긴다. 크기는 `storageKey` 에 남기고, 같은 키를 쓰는 다른 표에도 바로 알린다.
 */
export function useTablePaging<T>(
  rows: T[],
  resetKey?: unknown,
  storageKey: string = DAILY_TABLE_PAGE_SIZE_KEY,
): TablePaging<T> {
  const [pageSize, setPageSizeState] = useState<number>(() => readPageSize(storageKey));
  const [page, setPageState] = useState(1);

  const [resetCount, setResetCount] = useState(0);
  useEffect(() => { setPageState(1); setResetCount((c) => c + 1); }, [resetKey]);

  // 같은 키를 쓰는 다른 표(같은 화면의 다른 탭)가 크기를 바꾸면 따라간다.
  useEffect(() => {
    const onSize = (e: Event) => {
      const d = (e as CustomEvent<{ key: string; size: number }>).detail;
      if (d && d.key === storageKey) setPageSizeState(d.size);
    };
    window.addEventListener(SIZE_EVENT, onSize);
    return () => window.removeEventListener(SIZE_EVENT, onSize);
  }, [storageKey]);

  const total = rows.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, maxPage);
  // 줄이 줄어 지금 페이지가 비면 저장된 페이지도 당긴다 — 표시만 당기면 목록이 다시 늘 때 넘기지도 않은 높은 페이지로
  // 돌아갔다(코덱 리뷰 #209-3, 감사 card-controltower#283).
  // 함수형으로 — 탭 · 필터가 바뀐 같은 커밋의 1페이지 초기화를 덮어 마지막 페이지로 가던 것(코덱 셀프 리뷰 중간).
  useEffect(() => { setPageState((p) => (p > maxPage ? maxPage : p)); }, [page, maxPage]);
  const pageRows = useMemo(
    () => rows.slice((current - 1) * pageSize, current * pageSize),
    [rows, current, pageSize],
  );

  const setPage = useCallback((p: number) => setPageState(Math.max(1, p)), []);
  const setPageSize = useCallback((size: number) => {
    // 크기를 바꿔도 지금 보던 첫 줄이 들어 있는 페이지로 간다.
    setPageState((p) => Math.floor(((Math.min(p, maxPage) - 1) * pageSize) / size) + 1);
    setPageSizeState(size);
    writePageSize(storageKey, size);
    window.dispatchEvent(new CustomEvent(SIZE_EVENT, { detail: { key: storageKey, size } }));
  }, [maxPage, pageSize, storageKey]);

  const viewKey = `${current}|${pageSize}|${resetCount}`;
  return { page: current, pageSize, total, maxPage, pageRows, setPage, setPageSize, viewKey };
}

/** "1~50 / 92" — 0건이면 "0건". */
export function pageRangeLabel(page: number, pageSize: number, total: number): string {
  if (total <= 0) return '0건';
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return `${start.toLocaleString()}~${end.toLocaleString()} / ${total.toLocaleString()}`;
}

/** ← → 를 페이지 넘김으로 쓰면 안 되는 자리 — 글 입력 · 선택 상자 · 탭 목록 · 슬라이더 · 열린 창. */
function isArrowKeyTaken(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"]')) return true;
  if (target.closest('[role="tab"], [role="tablist"], [role="slider"], [role="radiogroup"], [role="menu"], [role="listbox"], [role="combobox"], [role="spinbutton"]')) return true;
  if (target.closest('.ant-select, .ant-select-dropdown, .ant-picker, .ant-picker-dropdown, .ant-pagination-simple-pager')) return true;
  return false;
}

/**
 * 화면에 실제로 보이는 창(모달 · Drawer)이 있는가. 닫혀도 DOM 에 남는 [role="dialog"](좁은 폭 사이드바 Drawer 등)를 열림으로
 * 세면 한 번 열었다 닫은 뒤로 ← → 가 조용히 꺼졌다(Opus 리뷰 PR #52 중간) — 그려진 상자가 있는 것만 센다.
 */
function hasOpenDialog(): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return Array.from(document.querySelectorAll('.ant-modal-wrap, .ant-drawer-open, [role="dialog"]')).some((el) => {
    if (getComputedStyle(el).visibility === 'hidden') return false;
    // 닫힌 Drawer 는 화면 밖으로 밀려나 있을 뿐 상자가 남는다 — 화면과 겹치는 상자만 연 창으로 본다.
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 && r.left < vw && r.top < vh;
  });
}

/**
 * 포커스가 이 페이지 줄이 붙은 표 근처(같은 부모 영역 안)이거나 아무 데도 없을 때(body)만 ← → 를 받는다 — 사이드바 · 헤더 등
 * 다른 자리에서 누른 화살표까지 페이지 넘김으로 가로채지 않게(감사 card-controltower#122).
 */
function isNearPager(target: EventTarget | null, pager: HTMLElement | null): boolean {
  if (!(target instanceof Element) || target === document.body || target === document.documentElement) return true;
  const area = pager?.parentElement;
  return !!area && area.contains(target);
}

/**
 * ← → 가 가로 스크롤에 쓰일 수 있는 자리인가 — 포커스(없으면 문서)에서 위로 올라가며 가로로 넘치는 스크롤 상자가 있으면
 * 페이지 넘김으로 가로채지 않는다(Opus 리뷰 PR #52 낮음 — 표 · 화면을 키보드로 가로 스크롤하던 것을 막지 않게).
 */
function isInHorizontalScroller(target: EventTarget | null): boolean {
  let el: Element | null = target instanceof Element ? target : null;
  while (el && el !== document.body && el !== document.documentElement) {
    const he = el as HTMLElement;
    if (he.scrollWidth > he.clientWidth + 1) {
      const ox = getComputedStyle(he).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    el = el.parentElement;
  }
  const root = document.scrollingElement as HTMLElement | null;
  return !!root && root.scrollWidth > root.clientWidth + 1;
}

export interface TablePagerProps<T> {
  paging: TablePaging<T>;
  /** 표 위 · 아래 어느 쪽인지 — 화면 낭독기 이름과 키보드(아래 줄만)에 쓴다. */
  placement: 'top' | 'bottom';
  /** 체크했지만 지금 페이지 밖에 있는 줄 수. */
  otherPageSelected?: number;
  /** 무엇의 페이지인지(화면 낭독기 이름). */
  label?: string;
  /** 덧붙일 class — 화면 위에 붙는 줄이 있는 표는 scroll-margin-top 을 주어 맨 위로 갈 때 그 줄에 가리지 않게 한다. */
  className?: string;
}

export function TablePager<T>({ paging, placement, otherPageSelected = 0, label = '표', className }: TablePagerProps<T>) {
  const t = useThemeTokens();
  const phone = useIsPhone();
  const ref = useRef<HTMLDivElement>(null);
  const { page, pageSize, total, maxPage, setPage, setPageSize, viewKey } = paging;

  // 페이지를 넘기거나 탭 · 필터 · 크기를 바꾸면 표 스크롤을 맨 위로 — 내장 페이지 넘김을 끄고 줄만 바꾸면서 스크롤은 그대로라 표
  // 아래에서 넘기면 새 페이지도 아래쪽부터 보였다(코덱 리뷰 #209-4, 감사 card-controltower#284). 위 줄 하나만 맡는다.
  const firstViewRef = useRef(true);
  useEffect(() => {
    if (placement !== 'top') return;
    if (firstViewRef.current) { firstViewRef.current = false; return; }
    const area = ref.current?.parentElement;
    if (!area) return;
    area.querySelectorAll<HTMLElement>('.ant-table-body, .ant-table-tbody-virtual-holder').forEach((el) => { el.scrollTop = 0; });
    const top = ref.current;
    // 화면 위에 붙는 헤더 · 선택 줄 밑에 가려진 경우도 위로 올린다(scroll-margin-top 만큼 — 없으면 0, 감사 #326).
    const margin = top ? parseFloat(getComputedStyle(top).scrollMarginTop) || 0 : 0;
    if (top && top.getBoundingClientRect().top < margin && typeof top.scrollIntoView === 'function') {
      top.scrollIntoView({ block: 'start' });
    }
  }, [placement, viewKey]);

  // ← → 키 — 아래 줄 하나만 듣는다(위 · 아래 둘이 함께 들으면 두 번 넘어간다).
  useEffect(() => {
    if (placement !== 'bottom') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (!ref.current || !ref.current.isConnected || (ref.current.offsetParent === null && !isJsdom())) return;
      if (isArrowKeyTaken(e.target) || hasOpenDialog() || isInHorizontalScroller(e.target)) return;
      if (!isNearPager(e.target, ref.current)) return;
      const next = e.key === 'ArrowLeft' ? page - 1 : page + 1;
      if (next < 1 || next > maxPage) return;
      e.preventDefault();
      setPage(next);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [placement, page, maxPage, setPage]);

  if (total === 0) return null;

  return (
    <div
      ref={ref}
      className={className ? `table-pager ${className}` : 'table-pager'}
      role="navigation"
      aria-label={`${label} 페이지(${placement === 'top' ? '위' : '아래'})`}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
        gap: SPACING.sm, padding: `${SPACING.xs}px ${SPACING.sm}px`,
      }}
    >
      <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, marginRight: 'auto' }}>
        <span data-testid={`pager-range-${placement}`} style={{ whiteSpace: 'nowrap' }}>{pageRangeLabel(page, pageSize, total)}</span>
        {otherPageSelected > 0 && (
          <span style={{ color: t.warning, whiteSpace: 'nowrap' }}>{` · 다른 페이지 ${otherPageSelected.toLocaleString()}건 선택`}</span>
        )}
      </span>
      {/* 번호와 크기 선택을 따로 그려 둘 사이 간격을 고정한다 — 내장 크기 선택은 번호에 붙어 보였다(#115). 휴대폰은 범위 ·
          크기 선택을 한 줄에, 번호를 다음 줄 가운데에. */}
      {maxPage > 1 && (
        <div style={phone ? { order: 3, flexBasis: '100%', display: 'flex', justifyContent: 'center' } : undefined}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
            simple={phone}
            showLessItems
            size={phone ? undefined : 'small'}
          />
        </div>
      )}
      <Select
        aria-label="페이지당 줄 수"
        size={phone ? 'large' : 'small'}
        value={pageSize}
        onChange={(v) => setPageSize(v)}
        options={TABLE_PAGE_SIZES.map((n) => ({ value: n, label: `${n.toLocaleString()} / 페이지` }))}
        style={{ width: phone ? 148 : 128, marginLeft: phone ? undefined : SPACING.xs }}
        popupMatchSelectWidth={false}
      />
    </div>
  );
}

/** jsdom 은 레이아웃이 없어 offsetParent 가 늘 null — 테스트에서는 보이는지 검사를 건너뛴다. */
function isJsdom(): boolean {
  return typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
}

// ─── 큰 페이지(500 · 1000)는 가상 스크롤 ───────────────────────────────────────────────────────────────
//
// 1,000줄을 한 번에 그리면 1280 · 390 모두 약 1초 멈췄다(2026-10-26 e2e 측정) — 500줄 이상이면 antd Table 의 virtual
// (보이는 줄만 그림)을 켠다. virtual 은 scroll.x · scroll.y 가 숫자여야 해서, "calc(100vh - Npx)" 꼴의 높이를 지금 창
// 높이로 풀어 숫자로 준다.
export const VIRTUAL_FROM_PAGE_SIZE = 500;

/** "calc(100vh - 532px)" · 숫자 · "480px" → 픽셀 수. 풀 수 없으면 창 높이의 60%. 최소 320. */
export function resolveScrollY(scrollY: string | number | undefined, viewportHeight: number): number {
  let px: number | null = null;
  if (typeof scrollY === 'number') px = scrollY;
  else if (typeof scrollY === 'string') {
    const calc = /^calc\(\s*100vh\s*-\s*(\d+(?:\.\d+)?)px\s*\)$/.exec(scrollY.trim());
    const plain = /^(\d+(?:\.\d+)?)px$/.exec(scrollY.trim());
    if (calc) px = viewportHeight - Number(calc[1]);
    else if (plain) px = Number(plain[1]);
  }
  if (px == null || !Number.isFinite(px)) px = Math.round(viewportHeight * 0.6);
  return Math.max(320, Math.round(px));
}

/** 창 높이를 따라가는 가상 스크롤 높이. */
export function useVirtualScrollY(scrollY: string | number | undefined): number {
  const [h, setH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  useEffect(() => {
    const onResize = () => setH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return resolveScrollY(scrollY, h);
}

/**
 * 가상 스크롤 표의 scroll.x — 열 폭(width, 없으면 minWidth, 그것도 없으면 120)의 합 + 선택 칸. 숫자를 열 합보다 작게 주면 오른쪽
 * 열이 좁아져 처리 버튼이 잘렸다(딥씨 셀프 리뷰 PR #52 중간).
 */
export function columnsScrollX(
  columns: ReadonlyArray<{ width?: number | string; minWidth?: number }>,
  selectionWidth = 0,
): number {
  return columns.reduce((sum, c) => {
    const w = typeof c.width === 'number' ? c.width : typeof c.minWidth === 'number' ? c.minWidth : 120;
    return sum + w;
  }, selectionWidth);
}
